import React, { useEffect, useRef, useState } from "react";
import { BREAK_POINT, BREAK_POINT_HINT, formatMoney, isWeakTier, laneColor, laneLabel, matchLabel, nodeKey, severityClass, tierStroke } from "./fusionApi";

const STATUS = {
  identified: ["Open", "bg-red-50 text-red-700"],
  in_progress: ["In progress", "bg-amber-50 text-amber-700"],
};

/** A risk's status chip as the sample shows it; an object shows its worst severity instead. */
const StatusChip = ({ member }) => {
  if (member.type !== "Risk") {
    return member.severity ? <span className={`text-[9px] px-1.5 py-0.5 rounded ${severityClass(member.severity)}`}>{member.severity}</span> : null;
  }
  const [label, cls] = STATUS[member.status] || [member.status || "Open", "bg-gray-100 text-gray-600"];
  return <span className={`text-[9px] px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
};

/**
 * The arrow into a hop: the $ carried in (omitted on the leading segment), the arrow stroked by the
 * hop's tier, a "from #n" caption when the hop joins an earlier hop than the previous one, the
 * join-point chips (each re-anchors), "why?" over the hop's links, and the tier text.
 */
const Arrow = ({ hop, index, carried, onOpen, onWhy, focusKeys }) => {
  const weak = isWeakTier(hop.tier);
  const stroke = hop.tier === "none" ? "#D1D5DB" : "#B8B6AE";
  const joinPoints = hop.joinPoints || [];
  const links = hop.links || [];
  return (
    <div className="flex-none w-28 flex flex-col items-center justify-center text-center px-0.5">
      {carried !== undefined && (
        <span className="text-[10px] px-1.5 rounded-full border border-border bg-theme-secondary text-content-secondary whitespace-nowrap mb-0.5"
          title="Correlated exposure carried into this risk">
          {formatMoney(carried)}
        </span>
      )}
      <svg viewBox="0 0 34 16" width="34" height="16" aria-hidden="true">
        <line x1="0" y1="8" x2="26" y2="8" stroke={stroke} strokeWidth="1.5" strokeDasharray={tierStroke(hop.tier)} />
        <path d="M24 3 L31 8 L24 13" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {hop.fromIndex != null && hop.fromIndex !== index - 1 && (
        <span className="text-[9px] text-content-muted">from #{hop.fromIndex + 1}</span>
      )}
      {joinPoints.length > 0 && (
        <span className="text-[9px] text-content-secondary leading-tight">
          {joinPoints.map((ref, k) => (
            <React.Fragment key={nodeKey(ref.type, ref.id)}>
              {k > 0 && " · "}
              <button
                className={`hover:text-indigo-600 hover:underline ${focusKeys?.has(nodeKey(ref.type, ref.id)) ? "rounded bg-amber-100 px-0.5 font-semibold text-amber-800" : ""}`}
                title={`Centre the chain on ${ref.label}`} onClick={() => onOpen(ref.type, ref.id)}>
                {ref.label}
              </button>
            </React.Fragment>
          ))}
        </span>
      )}
      {links.length > 0 && (
        <button className="text-[9px] text-content-muted hover:text-indigo-600 hover:underline" onClick={() => onWhy(links)}>why?</button>
      )}
      <span className={`text-[9px] ${weak ? "text-violet-600" : "text-content-muted"}`}>{matchLabel(hop.tier, links)}</span>
    </div>
  );
};

/**
 * The chain left to right (FUSION.md §4.7): one numbered node per risk on the detail's risk path, and an
 * arrow into each hop carrying the exposure of the hops before it, what the join matched on, the shared
 * objects it crosses and a "why?" opening the join evidence. The break-point risk is flagged Break
 * Point. Clicking a node or a join point centres the chain on it. `focusKeys` (a list path segment the
 * user clicked) highlights those risks and objects and scrolls the first into view. A "More →" cue
 * appears while hops are hidden past the right edge.
 */
const FusionChainFlow = ({ detail, breakPointKey, contextByNode, onOpen, onWhy, focusKeys, labelOf }) => {
  const path = detail.riskPath || [];
  const scroller = useRef(null);
  const [moreRight, setMoreRight] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const update = () => setMoreRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [path.length]);

  useEffect(() => {
    if (!focusKeys || focusKeys.size === 0) return;
    scroller.current?.querySelector?.("[data-focused='true']")?.scrollIntoView?.({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [focusKeys, detail]);

  if (!path.length) return <div className="text-sm text-content-muted py-4">No risks to draw on this chain.</div>;

  // A vendor already on the path as a join point needs no "linked vendor" chip on its risks.
  const vendorsOnPath = new Set(path.flatMap((h) => (h.joinPoints || []).filter((r) => r.type === "Vendor").map((r) => String(r.id))));
  const chipText = (c) => {
    if (c.kind !== "Vendor") return c.label;
    const name = labelOf && c.refId != null ? labelOf("Vendor", c.refId) : null;
    return name && name !== `Vendor ${c.refId}` ? `Linked vendor: ${name}` : "Linked vendor";
  };

  return (
    <div className="relative">
    <div ref={scroller} className="overflow-x-auto pb-4 pt-1 px-1">
      <div className="inline-flex items-stretch">
        {path.map((hop, i) => {
          const m = hop.hop;
          const key = nodeKey(m.type, m.id);
          const isBreakPoint = key === breakPointKey;
          const isAnchor = m.type === detail.anchorType && m.id === detail.anchorId;
          const showArrow = i > 0 || (hop.joinPoints || []).length > 0;
          return (
            <React.Fragment key={key}>
              {showArrow && (
                <Arrow hop={hop} index={i} carried={i > 0 ? path[i - 1].cumulative?.mean : undefined} onOpen={onOpen} onWhy={onWhy} focusKeys={focusKeys} />
              )}
              <div
                data-focused={focusKeys?.has(key) ? "true" : undefined}
                className={`flex-none w-44 rounded-xl border p-2.5 ${isBreakPoint ? "border-violet-400 bg-violet-50" : "border-border bg-theme-secondary"} ${isAnchor ? "ring-2 ring-inset ring-indigo-300" : ""} ${focusKeys?.has(key) ? "outline outline-2 -outline-offset-2 outline-amber-400" : ""}`}
              >
                <button className="w-full text-left" onClick={() => onOpen(m.type, m.id)} title={isAnchor ? "Anchor" : "Centre the chain on this risk"}>
                  <div className="flex items-start justify-between gap-1">
                    <div>{isBreakPoint && <div className="text-violet-700 text-[10px] font-semibold uppercase mb-1" title={BREAK_POINT_HINT}>◆ {BREAK_POINT}</div>}</div>
                    <span className="text-[10px] font-semibold text-content-muted" aria-label={`Hop ${i + 1}`}>#{i + 1}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-content-muted mb-1">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: laneColor(m.lane) }} />
                    {laneLabel(m.lane)}
                  </div>
                  <div className="font-semibold text-[13px] leading-tight text-content-primary">{m.label}</div>
                  <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                    {m.severity && <span className={`text-[9px] px-1.5 py-0.5 rounded ${severityClass(m.severity)}`}>{m.severity}</span>}
                    <StatusChip member={m} />
                  </div>
                  <span className="block text-[10px] mt-1.5 text-content-secondary">{m.owner && m.owner.trim() ? m.owner : "Unassigned"}</span>
                  {(contextByNode[key] || [])
                    .filter((c) => !(c.kind === "Vendor" && vendorsOnPath.has(String(c.refId))))
                    .map((c, k) => (
                      <span key={k} title={`${c.tier} context, not a link`} className="inline-block mt-1 text-[9px] px-1.5 rounded border border-dashed border-violet-400 text-violet-700">{chipText(c)}</span>
                    ))}
                </button>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
    {moreRight && (
      <button type="button" onClick={() => scroller.current?.scrollBy?.({ left: 360, behavior: "smooth" })}
        className="absolute right-0 top-1/2 -translate-y-1/2 rounded-l-lg border border-border bg-theme-primary/90 px-2 py-1 text-xs text-indigo-700 shadow-sm">
        More →
      </button>
    )}
    </div>
  );
};

export default FusionChainFlow;
